import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useTimer from "@repo/shopify/hooks/useTimer";
import type { IProductDetailNode, IProductDetailVariants } from "@repo/shopify/queries/product-gql";
import { extractId } from "@repo/shopify/utils/common";
import { resizeImage } from "@repo/shopify/utils/image";
import type { Metafield } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import ColorSelector from "@molecules/ColorSelector";

import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";

interface IProps {
  title: string;
  color: string[];
  variants: IProductDetailVariants | undefined;
  releaseDate?: Metafield;
  tags?: string[];
}

export default function ProductCard({ title, color, variants, releaseDate, tags }: IProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const { isDanielJohnston } = useCheckLocation();

  const [selectedColor, setSelectedColor] = useState(color[0]);
  const [selectedVariantId, setSelectedVariantId] = useState(extractId(variants?.edges?.[0].node.id || ""));

  const [isHover, setIsHover] = useState(false);

  const releaseDateMemo = useMemo(() => (releaseDate ? new Date(releaseDate.value) : null), [releaseDate]);

  const { formattedTimer, watch } = useTimer({ dueDate: releaseDateMemo ?? new Date(), type: "increase" });

  const isUpcoming = Boolean(releaseDateMemo && releaseDateMemo > watch);

  // 선택한 색상의 전체 목록
  const selectedProductList = useMemo(
    () =>
      variants?.edges.filter(
        ({ node }) => node.selectedOptions.find((item) => item.name === "Color")?.value === selectedColor,
      ),
    [selectedColor],
  );

  // 전체 목록 중 판매 가능 한 상품 목록
  const variantPrice = useMemo(() => variants?.edges.find(({ node }) => node.availableForSale), [variants]);

  const colorList = useMemo(() => {
    const list: IProductDetailNode[] = [];

    color.forEach((colorItem) => {
      variants?.edges
        .filter(({ node }) => (pathname.includes("archive") ? node.availableForSale : true))
        .forEach(({ node }) => {
          const optionColor = node.selectedOptions.find((option) => option.value === colorItem);

          if (optionColor) {
            const findList = list.find(
              (item) => item.selectedOptions.find((option) => option.value === colorItem)?.value === colorItem,
            );
            if (!findList) list.push(node);
          }
        });
    });

    return list;
  }, [variants]);

  const handleSelectColor = (item: IProductDetailNode) => {
    const itemColor = item.selectedOptions.find((option) => option.name === "Color")?.value || "";
    if (color) {
      setSelectedColor(itemColor);
    }

    setSelectedVariantId(extractId(item.id));
  };

  return (
    <article className="flex flex-col overflow-hidden">
      {/* 이미지 */}
      <Link
        className="relative"
        href={`${URLS.PRODUCTS}/${selectedProductList?.length ? selectedProductList?.[0]?.node.product.handle : ""}?variantId=${selectedVariantId}`}
        onMouseEnter={() => {
          setIsHover(true);
        }}
        onMouseLeave={() => {
          setIsHover(false);
        }}
      >
        <Image
          className={classNames(!isUpcoming && !variantPrice && "opacity-50")}
          height={1707}
          src={selectedProductList?.length ? resizeImage(selectedProductList[0]?.node.image?.url ?? "", 640) : ""}
          width={1280}
        />
      </Link>

      <div className="flex flex-col gap-2.5">
        {/* 컬러칩 */}
        {searchParams.get("view") && searchParams.get("view") === "combined" ? (
          <div className={classNames(!isUpcoming && !variantPrice && "opacity-50", "relative flex flex-wrap gap-1")}>
            {colorList?.map((item) => (
              <ColorSelector
                className="size-6"
                key={item.id}
                onClick={() => {
                  handleSelectColor(item);
                }}
                position={tags?.includes("Thumbnail Bottom") ? "bottom" : "center"}
                src={item.image?.url || ""}
              />
            ))}
          </div>
        ) : null}

        {/* 상품명 / 가격 */}
        <section className="flex gap-0.5">
          <Link
            className="pc:gap-1 relative flex! w-full flex-col"
            href={`${URLS.PRODUCTS}/${selectedProductList?.length ? selectedProductList?.[0]?.node.product.handle : ""}?variantId=${selectedVariantId}`}
          >
            <span
              className={classNames(
                variantPrice && isPC && isHover ? "hidden" : "block",
                "w-full",
                isUpcoming || variantPrice
                  ? ["text-black dark:text-white"]
                  : [isDanielJohnston ? "text-dj-grey" : "text-grey"],
              )}
            >
              {title}
            </span>
            {variantPrice ? (
              <span
                className={classNames(
                  isPC && isHover ? "flex" : "hidden",
                  "flex w-[calc(100%-4.5rem)] flex-wrap gap-2 gap-x-2 gap-y-1",
                )}
              >
                {selectedProductList?.map(({ node }) => (
                  <span
                    className={classNames(
                      "",
                      !isUpcoming && !node.availableForSale && [isDanielJohnston ? "text-dj-grey" : "text-grey"],
                    )}
                    key={node.id}
                  >
                    {node.selectedOptions.find((item) => item.name === "Size")?.value}
                  </span>
                ))}
              </span>
            ) : null}

            <Price
              availableForSale={isUpcoming || Boolean(variantPrice)}
              compareAtPrice={parseFloat(
                variantPrice?.node?.compareAtPrice?.amount ||
                  selectedProductList?.[0]?.node?.compareAtPrice?.amount ||
                  "0",
              )}
              direction={isPC ? "horizontal" : "horizontal"}
              price={parseFloat(
                variantPrice?.node?.price?.amount || selectedProductList?.[0]?.node?.price?.amount || "0",
              )}
            />
          </Link>

          {releaseDateMemo && releaseDateMemo > watch ? (
            <section className="text-blue flex flex-col gap-0.5">
              <span>Upcoming Item</span>
              <div className="flex">
                {`${formattedTimer.days}d : ${formattedTimer.hours}h : ${formattedTimer.minutes}m : ${formattedTimer.seconds}s`}
              </div>
            </section>
          ) : null}
        </section>
      </div>
    </article>
  );
}
